import React, { useState, useRef } from "react";
import { Formik, Form, Field } from "formik";
import { Row, Col, Collapse } from "reactstrap";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";

import {
  LabeledDatePicker,
  Select,
  FieldSpy,
} from "../../../../../components/formik";

import {
  truePositiveTypes,
  falsePositiveTypes,
  riskLevelTypes,
  scoringStatusTypes,
  clientStatusTypes,
  MULTIPLE_CLIENTS_FILTERS_LABELS,
  initialFiltersMultipleClientsReport,
} from "../../../constants/multipleClientsReportConstants";

import { ButtonWithSpinner } from "../../../../../components/common";

import {
  isLoadingSelector,
  multipleClientsReportFiltersSelector,
} from "../../../selectors";
import Clickable from "../../../../../components/common/Clickable";
import { CollapseButton, CollapseOpenIcon } from "../../../../../assets";
import ButtonSelectMultipleClients from "../../../../../components/formik/multiple-clients/ButtonSelectMultipleClients";
import ButtonSelectScoringStatus from "../../../../../components/formik/multiple-clients/ButtonSelectScoringStatus";
import { FaTimes } from "react-icons/fa";
import { CLIENT_TYPE_LABELS } from "../../../../monitoring-queue-entry/constants/monitoringQueueEntryConstants";
import {
  getClientStatusFilterLabel,
  getFalsePositiveMarkerFilterLabel,
  getRiskLevelFilterLabel,
  getScoringStatusFilterLabel,
  getTruePositiveMarkerFilterLabel,
} from "./multipleClientsFilterHelpers";
import { setMultipleClientsReportSearchFilters } from "../../../actions";
import {
  DATE_WITH_CURRENT_YEAR,
  DATE_WITH_MIN_YEAR,
} from "../../../../../utilities/common";
import { mainClientTypeSelector } from "../../../../../selectors/getScoreCardByTypeSelectors";

const MultipleClientsFilter = ({
  toggleCollapse,
  isOpenCollapse,
  multipleClientsReport,
  setIsSubmitted,
}) => {
  const dispatch = useDispatch();
  const [statuses, setStatuses] = useState(scoringStatusTypes);
  const formikRef = useRef(null);

  const filters = useSelector(multipleClientsReportFiltersSelector);

  const isLoading = useSelector(isLoadingSelector);

  const clientTypes = useSelector(mainClientTypeSelector);

  const handleSubmit = (data) => {
    if (data.to !== null) {
      data.to = data.to.replace("00:00:00", "23:59:59");
    }
    dispatch(setMultipleClientsReportSearchFilters(data));
    setIsSubmitted(true);
    toggleCollapse();
  };

  const handleRiskLevelChange = (type) => {
    setStatuses(
      scoringStatusTypes.filter((status) => {
        if (type === 5) {
          return [1, 2, 3, 4].includes(status.value);
        } else if (type === 3) {
          return status.value === 5;
        } else if (type === 1) {
          return status.value === 6;
        } else {
          return status.value !== null;
        }
      }),
    );

    formikRef.current.setFieldValue("scoringStatus", null);
    dispatch(setMultipleClientsReportSearchFilters({ scoringStatus: null }));
  };

  return (
    <Formik ref={formikRef} onSubmit={handleSubmit} initialValues={filters}>
      {(props) => {
        const { values } = props;
        const { from: dateFromValue, to: dateToValue } = values;
        return (
          <div>
            <div className="multiple-clients-collapse-search">
              <Collapse isOpen={isOpenCollapse}>
                <Form className="multiple-clients-entries-form">
                  <div className="multiple-clients-entries-form-inner-box">
                    <Row>
                      <Col lg={4} className="p-0">
                        <Row>
                          <Col lg={6}>
                            <LabeledDatePicker
                              dateFormat="dd.MM.yyyy"
                              name="from"
                              placeholderText="Date From"
                              label=""
                              minDate={DATE_WITH_MIN_YEAR}
                              maxDate={
                                dateToValue
                                  ? new Date(dateToValue)
                                  : DATE_WITH_CURRENT_YEAR
                              }
                              openToDate={new Date(moment())}
                            />
                          </Col>
                          <Col lg={6}>
                            <LabeledDatePicker
                              dateFormat="dd.MM.yyyy"
                              name="to"
                              placeholderText="Date To"
                              label=""
                              maxDate={DATE_WITH_CURRENT_YEAR}
                              minDate={
                                dateFromValue
                                  ? new Date(dateFromValue).getTime()
                                  : DATE_WITH_MIN_YEAR
                              }
                              openToDate={new Date(moment())}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col lg={4}>
                        <Field
                          type="text"
                          name="clientStatus"
                          placeholder="Client Status"
                          className="dropdown-group"
                          component={Select}
                          options={clientStatusTypes}
                          isClearable
                        />
                      </Col>
                      <Col lg={4}>
                        <Field
                          type="text"
                          name="falsePositiveMarker"
                          placeholder="False Positive Marker"
                          component={Select}
                          className="dropdown-group"
                          options={falsePositiveTypes}
                          isClearable
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={4} className="p-0">
                        <Field
                          type="text"
                          name="clientType"
                          placeholder="Client Type"
                          component={ButtonSelectMultipleClients}
                          options={clientTypes}
                          isClearable
                        />
                      </Col>
                      <Col lg={4}>
                        <Field
                          type="text"
                          name="riskLevel"
                          placeholder="Risk Level"
                          component={Select}
                          options={riskLevelTypes}
                          className="dropdown-group"
                          isClearable
                        />
                        <FieldSpy
                          name="riskLevel"
                          onChange={handleRiskLevelChange}
                        />
                      </Col>
                      <Col lg={4}>
                        <Field
                          type="text"
                          name="trueMatchMarker"
                          placeholder="True Positive Marker"
                          component={Select}
                          className="dropdown-group"
                          options={truePositiveTypes}
                          isClearable
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={12}>
                        <Field
                          type="text"
                          name="scoringStatus"
                          placeholder="Scoring Status"
                          className="dropdown-group"
                          component={ButtonSelectScoringStatus}
                          options={statuses}
                          isClearable
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        lg={12}
                        className="d-flex justify-content-center pt-4"
                      >
                        <ButtonWithSpinner
                          id="getMultipleClientsReportSubmitButton"
                          text="Search"
                          outline
                          type="submit"
                          className="center multi-clients-report-search"
                          loading={isLoading}
                        />
                      </Col>
                    </Row>
                  </div>
                </Form>
                <div className="multiple-clients-menu-collapse-button">
                  {multipleClientsReport &&
                  Object.keys(multipleClientsReport).length ? (
                    <Clickable onClick={toggleCollapse}>
                      <CollapseButton />
                    </Clickable>
                  ) : null}
                </div>
              </Collapse>
            </div>
            <div className="multiple-clients-menu-collapse-button">
              <Clickable onClick={toggleCollapse}>
                {!isOpenCollapse ? <CollapseOpenIcon /> : null}
              </Clickable>
            </div>
            <div className="multiple-clients-filters-main-container">
              {Object.values(filters).some(
                (x) => x !== undefined && x !== null,
              ) ? (
                <div className="filters-menu-label">Filter Applied</div>
              ) : null}

              {Object.entries(filters).map(([key, value], i) => {
                return value !== undefined && value !== null ? (
                  <SingleFilter
                    key={i}
                    filterKey={key}
                    filterValue={value}
                    clearFilter={() => clearSingleFilter(key)}
                  />
                ) : null;
              })}

              {Object.values(filters).some(
                (x) => x !== undefined && x !== null,
              ) ? (
                <div
                  onClick={clearFilters}
                  className="filters-menu-label-clear"
                >
                  Clear All
                </div>
              ) : null}
            </div>
          </div>
        );
      }}
    </Formik>
  );

  function clearSingleFilter(filterKey) {
    const cleanVal = null;
    const currentFilters = { ...filters };

    dispatch(
      setMultipleClientsReportSearchFilters({
        [filterKey]: cleanVal,
      }),
    );

    currentFilters[filterKey] = cleanVal;
    formikRef.current.setFieldValue(filterKey, cleanVal);

    if (Object.values(currentFilters).filter(Boolean).length) {
      setIsSubmitted(true);
    } else {
      toggleCollapse();
    }
  }

  function clearFilters() {
    dispatch(
      setMultipleClientsReportSearchFilters(
        initialFiltersMultipleClientsReport,
      ),
    );
    formikRef.current.setValues(initialFiltersMultipleClientsReport);
    toggleCollapse();
  }

  function SingleFilter({
    filterKey = "",
    filterValue = "",
    clearFilter = () => {},
  }) {
    let currentValue = filterValue;
    if (filterKey === "clientType" && filterValue) {
      // set type label
      currentValue = CLIENT_TYPE_LABELS[filterValue];
    }

    if (filterKey === "clientStatus") {
      currentValue = getClientStatusFilterLabel(currentValue);
    }

    if (filterKey === "falsePositiveMarker") {
      currentValue = getFalsePositiveMarkerFilterLabel(currentValue);
    }

    if (filterKey === "trueMatchMarker") {
      currentValue = getTruePositiveMarkerFilterLabel(currentValue);
    }

    if (filterKey === "riskLevel") {
      currentValue = getRiskLevelFilterLabel(currentValue);
    }

    if (filterKey === "scoringStatus") {
      currentValue = getScoringStatusFilterLabel(currentValue);
    }

    if ((filterKey === "from" || filterKey === "to") && filterValue) {
      currentValue = moment(new Date(filterValue)).format("DD.MM.yyyy");
    }

    if (filterKey === "clientType" && filterValue) {
      currentValue = filterValue;
    }

    return (
      <div className="single-filter-box-multiple-clients">
        <div className="single-filter-label">
          {MULTIPLE_CLIENTS_FILTERS_LABELS[filterKey]}
        </div>
        <div className="single-filter-chip">
          {currentValue}
          <FaTimes
            className="remove-filter-icon"
            onClick={clearFilter}
            color="#707070"
          />
        </div>
      </div>
    );
  }
};

MultipleClientsFilter.propTypes = {};

export default MultipleClientsFilter;



// WEBPACK FOOTER //
// ./src/features/reports/pages/multiple-clients-report/multiple-clients-filter/MultipleClientsFilter.jsx